import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { ApiError, errorKeyFromCode, errorMessageKey, unwrap } from '../../api/api-error';
import { captchaToken } from '../../auth/captcha';
import { ensureOk, ME_KEY, useAuthOptions } from '../../auth/session';
import { Button, buttonClassName } from '../../components/ui/button';
import { AuthError, AuthLayout, AuthLinks } from '../../components/auth/auth-dialog';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';

const schema = z.object({
  email: z.string().trim().min(1, 'validation.required').pipe(z.email('validation.email')),
  password: z.string().min(1, 'validation.required'),
});
type Values = z.infer<typeof schema>;

export function LoginPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const options = useAuthOptions();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const login = useMutation({
    mutationFn: async (values: Values) =>
      unwrap(
        await api.POST('/api/v1/auth/login', {
          body: {
            ...values,
            captchaToken: await captchaToken(options.data?.recaptchaSiteKey ?? null, 'login'),
          },
        }),
      ),
    onSuccess: async (result) => {
      if (result.status === 'second_factor_required') {
        await navigate('/login/second-factor', { state: location.state as unknown });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      const from = (location.state as { from?: string } | null)?.from;
      await navigate(from ?? '/', { replace: true });
    },
  });

  const resend = useMutation({
    mutationFn: async () => {
      ensureOk(
        await api.POST('/api/v1/auth/email/resend', {
          body: {
            email: form.getValues('email'),
            captchaToken: await captchaToken(
              options.data?.recaptchaSiteKey ?? null,
              'resend_verification',
            ),
          },
        }),
      );
    },
  });

  const notVerified =
    login.error instanceof ApiError && login.error.code === 'AUTH_EMAIL_NOT_VERIFIED';
  const redirectError = params.get('error');

  return (
    <AuthLayout title={t('auth.login.title')}>
      {redirectError !== null && <AuthError>{t(errorKeyFromCode(redirectError))}</AuthError>}
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => login.mutate(values))}
        noValidate
      >
        <TextField
          control={form.control}
          name="email"
          type="email"
          label={t('auth.field.email')}
          autoComplete="email"
          autoFocus
        />
        <TextField
          control={form.control}
          name="password"
          type="password"
          label={t('auth.field.password')}
          autoComplete="current-password"
        />
        {login.isError && <AuthError>{t(errorMessageKey(login.error))}</AuthError>}
        {notVerified && (
          <div className="space-y-2">
            {resend.isSuccess ? (
              <Notice tone="success">{t('auth.verify.resent')}</Notice>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => resend.mutate()}
                disabled={resend.isPending}
              >
                {t('auth.verify.resend')}
              </Button>
            )}
          </div>
        )}
        <Button type="submit" className="mt-1 w-full" disabled={login.isPending}>
          {t('auth.login.submit')}
        </Button>
      </form>
      {options.data?.googleEnabled === true && (
        // Обычная ссылка, не fetch: вход через Google — переход браузера с редиректами.
        <a
          href={`/api/v1/auth/google?locale=${locale}`}
          className={buttonClassName({ variant: 'secondary', className: 'w-full' })}
        >
          {t('auth.google.signIn')}
        </a>
      )}
      <AuthLinks>
        <Link to="/register">{t('auth.login.toRegister')}</Link>
        <Link to="/forgot-password">{t('auth.login.forgot')}</Link>
      </AuthLinks>
    </AuthLayout>
  );
}
